import React, { useState } from 'react';
import { colors } from '../theme/tokens';

const IndustryManagerModal = ({ industries, onSave, onDelete, onClose }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editProducts, setEditProducts] = useState([]);
  const [newProduct, setNewProduct] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const selectedIndustry = industries.find(i => i._id === selectedId);

  const handleSelect = (industry) => {
    setSelectedId(industry._id);
    setEditName(industry.name);
    setEditProducts([...industry.products]);
    setNewProduct('');
    setAddingNew(false);
  };

  const handleStartNew = () => {
    setSelectedId(null);
    setEditName('');
    setEditProducts([]);
    setNewProduct('');
    setAddingNew(true);
  };

  const handleAddProduct = () => {
    const trimmed = newProduct.trim();
    if (!trimmed || editProducts.includes(trimmed)) return;
    setEditProducts(prev => [...prev, trimmed]);
    setNewProduct('');
  };

  const handleRemoveProduct = (product) => {
    setEditProducts(prev => prev.filter(p => p !== product));
  };

  const handleSave = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    await onSave({
      _id: addingNew ? undefined : selectedId,
      name: editName.trim(),
      products: editProducts
    });
    setSaving(false);
    if (addingNew) {
      setAddingNew(false);
      setEditName('');
      setEditProducts([]);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    setDeleting(true);
    await onDelete(selectedId);
    setDeleting(false);
    setSelectedId(null);
    setEditName('');
    setEditProducts([]);
  };

  const isDirty = selectedIndustry
    ? editName !== selectedIndustry.name ||
      JSON.stringify(editProducts) !== JSON.stringify(selectedIndustry.products)
    : addingNew && editName.trim().length > 0;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Manage Industries</h2>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        <div style={styles.body}>
          {/* Left: industry list */}
          <div style={styles.listPanel}>
            <button onClick={handleStartNew} style={styles.addIndustryBtn}>
              + New Industry
            </button>
            <div style={styles.industryList}>
              {industries.length === 0 && (
                <div style={styles.emptyList}>No industries yet</div>
              )}
              {industries.map(industry => (
                <button
                  key={industry._id}
                  onClick={() => handleSelect(industry)}
                  style={{
                    ...styles.industryListItem,
                    ...(selectedId === industry._id ? styles.industryListItemActive : {})
                  }}
                >
                  <span style={styles.industryListName}>{industry.name}</span>
                  <span style={styles.productCount}>
                    {industry.products.length} product{industry.products.length !== 1 ? 's' : ''}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Right: edit panel */}
          <div style={styles.editPanel}>
            {!selectedId && !addingNew ? (
              <div style={styles.editPlaceholder}>
                Select an industry to edit, or create a new one
              </div>
            ) : (
              <>
                <div style={styles.editSection}>
                  <label style={styles.editLabel}>Industry Name</label>
                  <input
                    autoFocus
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSave()}
                    style={styles.editInput}
                    placeholder="e.g. Mining"
                  />
                </div>

                <div style={styles.editSection}>
                  <label style={styles.editLabel}>
                    Products We Supply to This Industry
                  </label>
                  <div style={styles.productList}>
                    {editProducts.length === 0 && (
                      <div style={styles.emptyProducts}>No products added yet</div>
                    )}
                    {editProducts.map((product, idx) => (
                      <div key={idx} style={styles.productItem}>
                        <span style={styles.productBullet}>•</span>
                        <span style={styles.productText}>{product}</span>
                        <button
                          onClick={() => handleRemoveProduct(product)}
                          style={styles.removeProductBtn}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={styles.addProductRow}>
                    <input
                      value={newProduct}
                      onChange={e => setNewProduct(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddProduct()}
                      placeholder="Add a product..."
                      style={styles.addProductInput}
                    />
                    <button
                      onClick={handleAddProduct}
                      disabled={!newProduct.trim()}
                      style={styles.addProductBtn}
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div style={styles.editActions}>
                  {selectedId && (
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      style={styles.deleteBtn}
                    >
                      {deleting ? 'Deleting...' : '🗑 Delete Industry'}
                    </button>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={!isDirty || saving || !editName.trim()}
                    style={{
                      ...styles.saveBtn,
                      opacity: (!isDirty || !editName.trim()) ? 0.5 : 1
                    }}
                  >
                    {saving ? 'Saving...' : addingNew ? 'Create Industry' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: colors.overlay,
    zIndex: 10000,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  modal: {
    background: colors.surfaceSolid,
    color: colors.text,
    borderRadius: '14px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    width: '700px',
    maxWidth: '95vw',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${colors.border}`
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 22px',
    borderBottom: `2px solid ${colors.border}`,
    background: colors.surfaceSoft
  },
  title: {
    margin: 0, fontSize: '18px', fontWeight: '700', color: colors.text
  },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '20px',
    cursor: 'pointer', color: colors.textMuted, lineHeight: 1
  },
  body: {
    display: 'flex', flex: 1, overflow: 'hidden'
  },
  listPanel: {
    width: '220px',
    borderRight: `2px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0
  },
  addIndustryBtn: {
    margin: '12px',
    padding: '9px',
    background: '#2fb65c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  industryList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 8px 12px'
  },
  emptyList: {
    textAlign: 'center', color: colors.textFaint, fontSize: '13px',
    padding: '20px 0', fontStyle: 'italic'
  },
  industryListItem: {
    width: '100%', padding: '10px 12px',
    background: 'none', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    textAlign: 'left', marginBottom: '3px',
    display: 'flex', flexDirection: 'column', gap: '2px',
    transition: 'background 0.15s'
  },
  industryListItemActive: {
    background: colors.greenSoft, outline: '2px solid #2fb65c'
  },
  industryListName: {
    fontSize: '13px', fontWeight: '600', color: colors.text
  },
  productCount: {
    fontSize: '11px', color: colors.textMuted
  },
  editPanel: {
    flex: 1,
    padding: '20px 22px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  editPlaceholder: {
    textAlign: 'center', color: colors.textFaint, fontSize: '14px',
    marginTop: '60px', fontStyle: 'italic'
  },
  editSection: {
    display: 'flex', flexDirection: 'column', gap: '8px'
  },
  editLabel: {
    fontSize: '12px', fontWeight: '700', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px'
  },
  editInput: {
    padding: '10px 12px', fontSize: '15px', fontWeight: '600',
    border: `2px solid ${colors.border}`, borderRadius: '8px', outline: 'none',
    background: colors.inputBg, color: colors.text, transition: 'border-color 0.2s'
  },
  productList: {
    display: 'flex', flexDirection: 'column', gap: '6px',
    minHeight: '40px'
  },
  emptyProducts: {
    color: colors.textFaint, fontSize: '13px', fontStyle: 'italic', padding: '4px 0'
  },
  productItem: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '7px 10px',
    background: colors.surfaceSoft, borderRadius: '6px'
  },
  productBullet: { color: '#2fb65c', fontWeight: '700', fontSize: '14px' },
  productText: { flex: 1, fontSize: '13px', color: colors.text },
  removeProductBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: colors.textFaint, fontSize: '12px', lineHeight: 1, flexShrink: 0,
    padding: '2px'
  },
  addProductRow: {
    display: 'flex', gap: '8px', marginTop: '4px'
  },
  addProductInput: {
    flex: 1, padding: '8px 10px', fontSize: '13px',
    border: `1px solid ${colors.border}`, borderRadius: '6px', outline: 'none',
    background: colors.inputBg, color: colors.text
  },
  addProductBtn: {
    padding: '8px 16px', background: colors.surface2, color: colors.text,
    border: `1px solid ${colors.border}`, borderRadius: '6px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer', flexShrink: 0
  },
  editActions: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: 'auto', paddingTop: '12px', borderTop: `1px solid ${colors.borderSoft}`
  },
  deleteBtn: {
    padding: '9px 16px', background: 'none', color: colors.red,
    border: `1px solid ${colors.red}`, borderRadius: '7px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer'
  },
  saveBtn: {
    padding: '9px 22px', background: '#2fb65c', color: 'white',
    border: 'none', borderRadius: '7px',
    fontSize: '13px', fontWeight: '700', cursor: 'pointer'
  }
};

export default IndustryManagerModal;
